import { useState } from 'react';
import {
  Button,
  Tag,
  Input,
  Form,
  Select,
  Empty,
  Collapse,
  Card,
  Space,
  Tooltip,
  Modal,
  Descriptions,
} from 'antd';
import {
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type {
  BiddingCase,
  AnnotationData,
  QuestionDetail,
} from '../../types/index';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

interface QuestionPanelProps {
  caseData: BiddingCase | null;
  annotationData: AnnotationData | null;
  selectedQuestionId: string | null;
  onSelectQuestion: (questionId: string) => void;
  onPreviewDoc: () => void;
  onUpdateAnnotation: (data: AnnotationData) => void;
}

const QuestionPanelV2 = ({
  caseData,
  annotationData,
  selectedQuestionId,
  onSelectQuestion,
  onPreviewDoc,
  onUpdateAnnotation,
}: QuestionPanelProps) => {
  const [expandedQuestions, setExpandedQuestions] = useState<string[]>([]);
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [editingValues, setEditingValues] = useState<Record<string, any>>({});

  const handleQuestionExpand = (questionId: string) => {
    setExpandedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
    onSelectQuestion(questionId);
  };

  const handleFieldChange = (questionId: string, field: string, value: any) => {
    setEditingValues((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: value },
    }));
  };

  const handleSaveQuestion = (question: QuestionDetail) => {
    if (!annotationData) return;

    const updates = editingValues[question.item_id] || {};
    const updatedQuestions = annotationData.questionDetails.map((q) =>
      q.item_id === question.item_id ? { ...q, ...updates } : q
    );

    onUpdateAnnotation({
      ...annotationData,
      questionDetails: updatedQuestions,
    });

    // 清除编辑状态
    setEditingValues((prev) => {
      const newValues = { ...prev };
      delete newValues[question.item_id];
      return newValues;
    });
  };

  const getTopicColor = (topic: string) => {
    const colors: Record<string, string> = {
      资格条件: 'blue',
      技术参数: 'purple',
      报价评审: 'orange',
      程序流程: 'cyan',
      中标结果: 'green',
      人员要求: 'magenta',
    };
    return colors[topic] || 'default';
  };

  const getFieldValue = (question: QuestionDetail, field: string) => {
    return editingValues[question.item_id]?.[field] ?? question[field as keyof QuestionDetail];
  };

  return (
    <div className="annotation-panel">
      {/* 顶部标题栏 */}
      <div className="annotation-panel-header">
        <div className="flex items-center gap-2">
          <span>📋 质疑函 (输入端)</span>
          {annotationData && (
            <Tooltip title="查看完整JSON数据">
              <Button
                type="link"
               
                icon={<CodeOutlined />}
                onClick={() => setJsonModalVisible(true)}
              >
                JSON
              </Button>
            </Tooltip>
          )}
        </div>
        {caseData?.questionDoc && (
          <Button
            type="primary"
           
            icon={<EyeOutlined />}
            onClick={onPreviewDoc}
          >
            预览文档
          </Button>
        )}
      </div>

      <div className="annotation-panel-content">
        {!annotationData ? (
          <Empty description="请选择案件" />
        ) : (
          <>
            {/* 文档元数据 */}
            <Card className="mb-3" title="📋 文档元数据">
              <Descriptions column={2}>
                <Descriptions.Item label="文档类型">
                  {annotationData.docMeta.doc_type}
                </Descriptions.Item>
                <Descriptions.Item label="案件ID">
                  <span className="font-mono text-xs">
                    {annotationData.docMeta.case_id}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="项目名称" span={2}>
                  {annotationData.docMeta.project_name}
                </Descriptions.Item>
                <Descriptions.Item label="发文日期">
                  {annotationData.docMeta.issue_date}
                </Descriptions.Item>
                <Descriptions.Item label="质疑方">
                  {annotationData.docMeta.questioner?.name}
                </Descriptions.Item>
                <Descriptions.Item label="意图摘要" span={2}>
                  <Input.TextArea
                   
                    rows={2}
                    defaultValue={annotationData.docMeta.intent_summary}
                  />
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 质疑事项列表 - 折叠式 */}
            <div className="form-section">
              <div className="form-section-title">
                📌 质疑事项列表
                <span className="text-xs text-gray-400 ml-2">
                  ({annotationData.questionDetails.length}个)
                </span>
              </div>

              <Collapse
                activeKey={expandedQuestions}
                onChange={(keys) => setExpandedQuestions(keys as string[])}
                ghost
                className="question-collapse"
              >
                {annotationData.questionDetails.map((question, index) => (
                  <Panel
                    key={question.item_id}
                    header={
                      <div
                        className="flex items-center justify-between w-full pr-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuestionExpand(question.item_id);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            事项 #{question.index}
                          </span>
                          <Tag color={getTopicColor(question.topic)}>
                            {question.topic}
                          </Tag>
                          {selectedQuestionId === question.item_id && (
                            <CheckCircleOutlined className="text-primary" />
                          )}
                        </div>
                        <span className="text-sm text-gray-500 truncate max-w-[200px]">
                          {question.question_summary}
                        </span>
                      </div>
                    }
                    className={`mb-2 border rounded-lg overflow-hidden ${
                      selectedQuestionId === question.item_id
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    {/* 折叠内容：质疑详情编辑 */}
                    <div className="p-3 bg-white">
                      <Form layout="vertical">
                        <Form.Item label="主题">
                          <Select
                            value={getFieldValue(question, 'topic')}
                            onChange={(value) =>
                              handleFieldChange(question.item_id, 'topic', value)
                            }
                          >
                            <Option value="资格条件">资格条件</Option>
                            <Option value="技术参数">技术参数</Option>
                            <Option value="报价评审">报价评审</Option>
                            <Option value="程序流程">程序流程</Option>
                            <Option value="中标结果">中标结果</Option>
                            <Option value="人员要求">人员要求</Option>
                            <Option value="其他">其他</Option>
                          </Select>
                        </Form.Item>

                        <Form.Item label="质疑摘要">
                          <Input
                            value={getFieldValue(question, 'question_summary')}
                            onChange={(e) =>
                              handleFieldChange(
                                question.item_id,
                                'question_summary',
                                e.target.value
                              )
                            }
                          />
                        </Form.Item>

                        <Form.Item label="质疑详细内容">
                          <TextArea
                            rows={4}
                            value={getFieldValue(question, 'question_detail')}
                            onChange={(e) =>
                              handleFieldChange(
                                question.item_id,
                                'question_detail',
                                e.target.value
                              )
                            }
                          />
                        </Form.Item>

                        <Form.Item label="请求措施">
                          <Input
                            value={getFieldValue(question, 'requested_action')}
                            onChange={(e) =>
                              handleFieldChange(
                                question.item_id,
                                'requested_action',
                                e.target.value
                              )
                            }
                          />
                        </Form.Item>

                        <Form.Item label="事实依据">
                          <div className="space-y-2">
                            {(question.evidence_facts || []).map((fact, idx) => (
                              <div key={idx} className="flex gap-2">
                                <Input
                                  defaultValue={fact}
                                 
                                  className="flex-1"
                                />
                                <Button
                                 
                                  danger
                                  icon={<DeleteOutlined />}
                                />
                              </div>
                            ))}
                            <Button
                              type="dashed"
                             
                              block
                              icon={<PlusOutlined />}
                            >
                              添加依据
                            </Button>
                          </div>
                        </Form.Item>

                        <Form.Item label="援引法律法规">
                          <div className="space-y-2">
                            {(question.laws_regulations_cited || []).map(
                              (law, idx) => (
                                <Card key={idx} className="bg-gray-50">
                                  <div className="font-medium text-sm">
                                    《{law.name}》
                                  </div>
                                  {law.articles?.map((article, aidx) => (
                                    <div
                                      key={aidx}
                                      className="text-gray-600 text-xs mt-1 pl-2 border-l-2 border-gray-300"
                                    >
                                      第{article.article_index}条:{' '}
                                      {article.content?.slice(0, 100)}...
                                    </div>
                                  ))}
                                </Card>
                              )
                            )}
                            <Button
                              type="dashed"
                             
                              block
                              icon={<PlusOutlined />}
                            >
                              添加法规
                            </Button>
                          </div>
                        </Form.Item>

                        <Button
                          type="primary"
                          block
                          onClick={() => handleSaveQuestion(question)}
                        >
                          保存修改
                        </Button>
                      </Form>
                    </div>
                  </Panel>
                ))}
              </Collapse>

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                className="mt-3"
              >
                添加新事项
              </Button>
            </div>
          </>
        )}
      </div>

      {/* JSON数据弹窗 */}
      <Modal
        title="标注数据 (JSON)"
        open={jsonModalVisible}
        onCancel={() => setJsonModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setJsonModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {annotationData && (
          <div className="bg-gray-900 text-green-400 p-4 rounded overflow-auto max-h-[600px]">
            <pre className="text-xs whitespace-pre-wrap">
              {JSON.stringify(annotationData, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QuestionPanelV2;
